// Maintained by the ML team — treat as vendored code: don't refactor or optimize
// locally; replace the whole file when they ship a new version.
// Differences from the version they provided: linter formatting, their original
// (Serbian) comments removed, and the fitJohnsonSu error message translated to
// English. The executable code is otherwise identical, verified against their
// reference outputs.

function normCdf(z) {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp((-z * z) / 2);
  const p = d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return z >= 0 ? 1 - p : p;
}
function normPdf(z) {
  return 0.3989422804014327 * Math.exp((-z * z) / 2);
}
function normPpf(p) {
  const a = [-3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2, -3.066479806614716e1, 2.506628277459239];
  const b = [-5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1];
  const c = [-7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
  const pLow = 0.02425;
  let q;
  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  }
  if (p > 1 - pLow) {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  }
  q = p - 0.5;
  const r = q * q;
  return (
    ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
    (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
  );
}

const DELTA_FLOOR = 0.75;
const STARTING_DELTAS = [0.75, 1.5, 3.0, 8.0, 25.0, 100.0];
export const KNOT_WEIGHTS = [1, 2, 3, 2, 1];

function solve4(A, b) {
  const n = 4;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let k = 0; k < n; k++) {
    let pivot = k;
    for (let i = k + 1; i < n; i++) if (Math.abs(M[i][k]) > Math.abs(M[pivot][k])) pivot = i;
    [M[k], M[pivot]] = [M[pivot], M[k]];
    if (Math.abs(M[k][k]) < 1e-14) return null;
    for (let i = k + 1; i < n; i++) {
      const f = M[i][k] / M[k][k];
      for (let j = k; j <= n; j++) M[i][j] -= f * M[k][j];
    }
  }
  const x = [0, 0, 0, 0];
  for (let i = n - 1; i >= 0; i--) {
    let s = M[i][n];
    for (let j = i + 1; j < n; j++) s -= M[i][j] * x[j];
    x[i] = s / M[i][i];
  }
  return x;
}

function lmFit(z, y, w, x0) {
  let [xi, lam, gamma, delta] = x0;
  let lambda = 1e-3;
  const n = z.length;
  const cost = (p) => {
    let c = 0;
    for (let i = 0; i < n; i++) {
      const r = (p[0] + p[1] * Math.sinh((z[i] - p[2]) / p[3]) - y[i]) * w[i];
      c += r * r;
    }
    return 0.5 * c;
  };
  let c0 = cost([xi, lam, gamma, delta]);
  for (let it = 0; it < 300; it++) {
    const JtJ = [
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
    ];
    const Jtr = [0, 0, 0, 0];
    for (let i = 0; i < n; i++) {
      const u = (z[i] - gamma) / delta;
      const sh = Math.sinh(u),
        ch = Math.cosh(u);
      const r = (xi + lam * sh - y[i]) * w[i];
      const J = [w[i], sh * w[i], ((-lam * ch) / delta) * w[i], ((-lam * ch * u) / delta) * w[i]];
      for (let a = 0; a < 4; a++) {
        Jtr[a] += J[a] * r;
        for (let b = 0; b < 4; b++) JtJ[a][b] += J[a] * J[b];
      }
    }
    const A = JtJ.map((row, i) => row.map((v, j) => (i === j ? v * (1 + lambda) + 1e-12 : v)));
    const step = solve4(
      A,
      Jtr.map((v) => -v),
    );
    if (!step) break;
    const cand = [xi + step[0], Math.max(lam + step[1], 1e-9), gamma + step[2], Math.min(Math.max(delta + step[3], DELTA_FLOOR), 1e4)];
    const c1 = cost(cand);
    if (c1 < c0) {
      [xi, lam, gamma, delta] = cand;
      lambda = Math.max(lambda * 0.3, 1e-12);
      if (c0 - c1 < 1e-16 * Math.max(c0, 1e-16)) {
        c0 = c1;
        break;
      }
      c0 = c1;
    } else {
      lambda *= 10;
      if (lambda > 1e12) break;
    }
  }
  return { params: [xi, lam, gamma, delta], cost: c0 };
}

export function fitJohnsonSu(probs, values, weights = KNOT_WEIGHTS) {
  if (probs.length !== values.length || probs.length < 2) {
    throw new Error("need at least 2 (probability, value) pairs");
  }
  let v = [...values];
  let pointMass = false;
  if (v[v.length - 1] - v[0] <= 0) {
    pointMass = true;
    const n = v.length;
    v = v.map((x, i) => x * (1 + (-0.005 + (0.01 * i) / (n - 1))));
  }
  const z = probs.map(normPpf);
  const y = v.map(Math.log);
  const meanW = weights.reduce((a, b) => a + b, 0) / weights.length;
  const w = weights.map((x) => x / meanW);

  const n = z.length;
  const mz = z.reduce((a, b) => a + b, 0) / n;
  const my = y.reduce((a, b) => a + b, 0) / n;
  let num = 0,
    den = 0;
  for (let i = 0; i < n; i++) {
    num += (z[i] - mz) * (y[i] - my);
    den += (z[i] - mz) ** 2;
  }
  const slope = num / den;
  const intercept = my - slope * mz;

  let best = null;
  for (const start of STARTING_DELTAS) {
    const delta0 = Math.max(start, DELTA_FLOOR);
    const lam0 = Math.max(Math.abs(slope) * delta0, 1e-6);
    const attempt = lmFit(z, y, w, [intercept, lam0, 0, delta0]);
    if (!best || attempt.cost < best.cost) best = attempt;
  }
  const [xi, lam, gamma, delta] = best.params;
  return { xi, lam, gamma, delta, pointMass, probs: [...probs], values: v };
}

const P_FLOOR = 1e-4,
  P_CEIL = 1 - 1e-4;
const clampP = (p) => Math.min(Math.max(p, P_FLOOR), P_CEIL);

export function priceAt(fit, p) {
  const z = normPpf(clampP(p));
  return Math.exp(fit.xi + fit.lam * Math.sinh((z - fit.gamma) / fit.delta));
}
export function percentileOf(fit, x) {
  const u = (Math.log(x) - fit.xi) / fit.lam;
  return normCdf(fit.gamma + fit.delta * Math.asinh(u));
}
export function pdfAt(fit, x) {
  const u = (Math.log(x) - fit.xi) / fit.lam;
  const z = fit.gamma + fit.delta * Math.asinh(u);
  return (normPdf(z) * (fit.delta / (fit.lam * Math.sqrt(1 + u * u)))) / x;
}
export function winRate(p, side) {
  return side === "sell" ? 1 - p : p;
}

const P_MIN = 0.01,
  P_MAX = 0.99;
export const MAX_WIN_PCT = Math.round((P_MAX - P_MIN) * 1000) / 10;

export function sliderBounds(fit) {
  return { lo: priceAt(fit, P_MIN * 2), hi: priceAt(fit, 1 - (1 - P_MAX) * 2) };
}

export function solveBand(fit, centerPrice, mass) {
  const pC = percentileOf(fit, centerPrice);
  let pLo = pC - mass / 2,
    pHi = pC + mass / 2;
  if (pLo < P_MIN) {
    pLo = P_MIN;
    pHi = P_MIN + mass;
  } else if (pHi > P_MAX) {
    pLo = P_MAX - mass;
    pHi = P_MAX;
  }
  return {
    low: priceAt(fit, pLo),
    mid: centerPrice,
    high: priceAt(fit, pHi),
    pLo,
    pHi,
    pC,
    shifted: Math.abs((pLo + pHi) / 2 - pC) > 1e-9,
  };
}
